import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  TextField,
  Checkbox,
} from "@tiny/components";
import CreditCard from "@tiny/icons/generated/CreditCard";
import {
  useGetCreditBalanceQuery,
  useGetAccountQuery,
  usePostRetentionActivateMutation,
} from "@tiny/api";
import { useApi } from "@tiny/api";
import { makeStyles } from "@tiny/components";
import { formatNumber } from "@tiny/components/src/utils/formatUtils";
import { useTinyHost } from "@tiny/host-contract";

const MIN_CREDITS_REQUIRED = 200;
const DEFAULT_AUDIENCE_NAME = "Leads from Retention.com";
const DEFAULT_REACTIVATION_AUDIENCE_NAME = "Reactivation from Retention.com";

const useStyles = makeStyles((theme) => ({
  infoBox: {
    padding: theme.spacing(6),
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: theme.spacing(3),
  },
  creditBalanceCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  dialogActions: {
    padding: theme.spacing(6, 8),
    borderTop: `1px solid ${theme.palette.grey[100]}`,
    backgroundColor: theme.palette.grey[50],
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    gap: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column-reverse",
    },
  },
  cancelButton: {
    padding: theme.spacing(4, 6),
    fontWeight: 600,
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  proceedButton: {
    padding: theme.spacing(4, 8),
    fontWeight: 700,
    minWidth: 280,
    [theme.breakpoints.down("md")]: {
      width: "100%",
      minWidth: "auto",
    },
  },
}));

interface RetentionActivationDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  onSuccess: () => void;
}

function RetentionActivationDialog(props: RetentionActivationDialogProps) {
  const api = useApi();
  const { open, onClose, productId, onSuccess } = props;
  const classes = useStyles();
  const { navigate, capabilities } = useTinyHost();
  const isMaster = capabilities.accountType === "MASTER";

  const [isConfirming, setIsConfirming] = React.useState(false);
  const [website, setWebsite] = React.useState("");
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  const { mutateAsync: activateRetention } = usePostRetentionActivateMutation();

  const { data: balanceData, isLoading: isBalanceLoading } =
    useGetCreditBalanceQuery(productId, { enabled: open && !!productId });

  const { data: profileData } = useGetAccountQuery({ enabled: open });

  React.useEffect(() => {
    if (!open) {
      setWebsite("");
      setTermsAccepted(false);
      return;
    }
    if (profileData?.site) {
      setWebsite(profileData.site);
    }
  }, [open, profileData?.site]);

  const availableCredits = balanceData?.remainingCredits ?? 0;
  const hasMinCredits = availableCredits >= MIN_CREDITS_REQUIRED;

  const handleTopUp = () => {
    navigate.to(`/main/checkout?checkout_type=credits&product_id=${productId}`);
    onClose();
  };

  const resolveUniqueName = async (): Promise<string> => {
    let name = DEFAULT_AUDIENCE_NAME;
    let counter = 2;
    let { data: exists } = await api.getSegmentCheckExists({ name });
    while (exists) {
      name = `${DEFAULT_AUDIENCE_NAME} (${counter++})`;
      ({ data: exists } = await api.getSegmentCheckExists({ name }));
    }
    return name;
  };

  const resolveUniqueReactivationName = async (): Promise<string> => {
    let name = DEFAULT_REACTIVATION_AUDIENCE_NAME;
    let counter = 2;
    let { data: exists } = await api.getSegmentCheckExists({ name });
    while (exists) {
      name = `${DEFAULT_REACTIVATION_AUDIENCE_NAME} (${counter++})`;
      ({ data: exists } = await api.getSegmentCheckExists({ name }));
    }
    return name;
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const [uniqueName, uniqueReactivationName] = await Promise.all([
        resolveUniqueName(),
        resolveUniqueReactivationName(),
      ]);
      const [{ data: newSegment }, { data: reactivationSegment }] =
        await Promise.all([
          api.postSegmentUpload({ name: uniqueName }),
          api.postSegmentUpload({ name: uniqueReactivationName }),
        ]);
      await activateRetention({
        segmentId: newSegment.id!,
        reactivationSegmentId: reactivationSegment.id!,
        website,
      });
      onSuccess();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>Activate Retention.com Integration</DialogTitle>

      <DialogContent>
        {/* How it works */}
        <Box
          data-tour="retention-how-it-works"
          className={classes.infoBox}
          mb={4}
        >
          <Typography
            variant="h4"
            color="grey"
            tone={900}
            fontWeight="semiBold"
            component="div"
          >
            How it works!
          </Typography>
          <Typography
            variant="body3"
            color="grey"
            tone={700}
            component="div"
            mt={2}
          >
            Retention.com identifies anonymous visitors on your website and
            matches them to real email addresses.
          </Typography>
          <Typography
            variant="body3"
            color="grey"
            tone={700}
            component="div"
            mt={2}
          >
            Once activated, identified leads are automatically added to a new
            audience.
          </Typography>
          <Typography
            variant="body3"
            color="grey"
            tone={700}
            component="div"
            mt={2}
          >
            You will be charged credits per lead identified and added to your
            list.
          </Typography>
          <Typography
            variant="body3"
            color="grey"
            tone={700}
            component="div"
            mt={2}
          >
            A tracking script will be generated — install it on your website to
            start collecting leads.
          </Typography>
        </Box>

        {/* Audience Name */}
        <Box mb={4}>
          <TextField
            label="Audience Name"
            value={DEFAULT_AUDIENCE_NAME}
            InputProps={{ readOnly: true }}
            fullWidth
          />
        </Box>

        {/* Reactivation Audience Name */}
        <Box mb={4}>
          <TextField
            label="Reactivation Audience Name"
            value={DEFAULT_REACTIVATION_AUDIENCE_NAME}
            InputProps={{ readOnly: true }}
            fullWidth
          />
        </Box>

        {/* Website */}
        <Box data-tour="retention-website-field" mb={4}>
          <TextField
            label="Website"
            placeholder="https://example.com"
            value={website}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setWebsite(e.target.value)
            }
            fullWidth
          />
        </Box>

        {/* Credit balance */}
        <Box
          data-tour="retention-credit-balance"
          className={classes.creditBalanceCard}
        >
          <Typography variant="label3" color="grey" tone={500}>
            Available Balance
          </Typography>
          {isBalanceLoading ? (
            <Box display="flex" alignItems="center" mt={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
              <Typography
                variant="h3"
                color="grey"
                tone={900}
                fontWeight="bold"
              >
                {formatNumber(availableCredits)} Credits
              </Typography>
              <Chip
                label={hasMinCredits ? "Sufficient" : "Insufficient"}
                size="small"
                color={hasMinCredits ? "primary" : "red"}
              />
              {isMaster && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleTopUp}
                  startIcon={<CreditCard />}
                >
                  Top Up
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Insufficient credits warning */}
        {!isBalanceLoading && !hasMinCredits && (
          <Box mt={3}>
            <Alert severity="warning">
              A minimum of{" "}
              <strong>{formatNumber(MIN_CREDITS_REQUIRED)} credits</strong> is
              required to activate this integration.{" "}
              {isMaster ? (
                <strong
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={handleTopUp}
                >
                  Top Up Credits
                </strong>
              ) : (
                <>Please ask your master account to top up credits.</>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* Terms & Conditions */}
      <Box px={8} py={4} display="flex" alignItems="center" style={{ gap: 4 }}>
        <Checkbox
          color="primary"
          checked={termsAccepted}
          onChangeValue={(checked: boolean) => setTermsAccepted(checked)}
        />
        <Typography variant="body3" color="grey" tone={700} component="div">
          I understand that Retention.com is a third-party service and that my
          use of Retention.com is governed by Retention.com&apos;s{" "}
          <a
            href="https://retention.com/TinyEmails/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
          .
        </Typography>
      </Box>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          variant="text"
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          data-tour="retention-activate-btn"
          variant="contained"
          color="primary"
          disabled={
            isBalanceLoading || !hasMinCredits || isConfirming || !termsAccepted
          }
          onClick={handleConfirm}
          className={classes.proceedButton}
        >
          {isConfirming ? "Activating..." : "Activate Integration"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RetentionActivationDialog;
